import React from 'react';
import { ArrowDownIcon, ArrowRightIcon, LayoutTemplateIcon, SearchIcon, MessagesSquareIcon, ShoppingCartIcon, WorkflowIcon, ServerCogIcon } from 'lucide-react';
import { businessNeeds, systemCapabilities } from '../data/homepage';
const icons = {
  website: LayoutTemplateIcon,
  seo: SearchIcon,
  leads: MessagesSquareIcon,
  ecommerce: ShoppingCartIcon,
  integrations: WorkflowIcon,
  hosting: ServerCogIcon
} as const;
const RAIL = 'bg-brand-orange';
export function ServicesSection() {
  const lastIndex = businessNeeds.length - 1;
  return <section id="services" aria-labelledby="services-title" className="bg-paper" data-id="element-137" data-mp="2bd3bb63uo7oq">
      <div className="mx-auto max-w-[1280px] px-5 py-12 md:px-8 md:py-16 lg:py-20" data-id="element-138" data-mp="1xhr2xvwruipc">
        <p className="flex items-center gap-2 text-[13px] font-extrabold uppercase leading-5 text-brand-greenDeep" data-id="element-139" data-mp="22jgdzbjzuwdf">
          <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-brand-orange" aria-hidden data-id="element-140" data-mp="1nnr0y2wsdtku" />
          One connected system
        </p>
        <h2 id="services-title" className="mt-3 max-w-[720px] text-[30px] font-extrabold leading-[1.12] text-ink md:text-[42px]" data-id="element-141" data-mp="2pn18xjldmegc">
          Bring in business. Keep it moving.
        </h2>
        <p className="mt-4 max-w-[640px] text-[17px] leading-[1.6] text-ink/80" data-id="element-142" data-mp="2yo97m2rt1hay">
          We connect your website, marketing, and business tools around how you work. One team builds it, keeps it
          running, and improves it with you.
        </p>

        {/* Side-by-side chart from xl (1280+) where both sides have real width; stacked below. */}
        <div className="mt-10 grid md:mt-12 xl:grid-cols-[auto_minmax(0,1fr)] xl:items-center" data-id="element-143" data-mp="21vm51mdybp2z">
          {/* INPUTS: business needs + collector */}
          <div className="w-full md:mx-auto md:max-w-[560px] xl:mx-0 xl:w-auto xl:max-w-none" data-id="element-144" data-mp="2axc4nfhzlubu">
            <h3 className="text-[18px] font-bold leading-snug text-ink" data-id="element-145" data-mp="1n80z7kpozbrd">What your business needs</h3>
            <div className="mt-4 flex" data-id="element-146" data-mp="1j80ituj18qho">
              <ul className="w-full xl:w-[290px]" data-id="element-147" data-mp="1j54oeg6e8jxz">
                {businessNeeds.map((need, i) => {
                const isFirst = i === 0;
                const isLast = i === lastIndex;
                return (
                  // Rows touch (no grid gap); the 8px visual gap comes from the box's vertical margin,
                  // so each row's rail segment spans its full height and the rail stays continuous.
                  <li key={need} className="flex items-stretch" data-id="element-148" data-mp="2zdcztn6grkqa" data-mpi={i}>
                      <div className="my-1 flex min-w-0 flex-1 items-center rounded-[6px] border border-l-[3px] border-mint-line border-l-brand-green bg-mint px-4 py-3 text-[16px] font-bold leading-snug text-ink" data-id="element-149" data-mp="2zf7mp71apmx0">
                        {need}
                      </div>
                      {/* Desktop feeder: horizontal stub at this row's centre + this row's slice of the rail */}
                      <div className="relative hidden w-7 shrink-0 xl:block" aria-hidden data-id="element-150" data-mp="oksanjytecj5">
                        <span className={`absolute left-0 right-0 top-1/2 h-[3px] -translate-y-1/2 ${RAIL}`} data-id="element-151" data-mp="tilg2asan2id" />
                        <span className={`absolute right-0 w-[3px] ${RAIL} ${isFirst ? 'top-1/2 bottom-0' : isLast ? 'top-0 bottom-1/2' : 'inset-y-0'}`} data-id="element-152" data-mp="2e1dzrc7hhsxs" />
                      </div>
                    </li>
                );
              })}
              </ul>

              {/* Desktop bridge: from the rail (at the list's vertical centre, which always lies on the
                  rail) to a prominent arrow pointing into the solutions. */}
              <div className="relative hidden w-[104px] shrink-0 xl:block" aria-hidden data-id="element-153" data-mp="1ld03b0w1n4oc">
                <span className="absolute left-0 top-1/2 flex w-full -translate-y-1/2 items-center pr-2" data-id="element-154" data-mp="1geotgq5r0u64">
                  <span className={`h-[3px] flex-1 ${RAIL}`} data-id="element-155" data-mp="3fs2myhyevsjc" />
                  <ArrowRightIcon className="-ml-3 h-8 w-8 shrink-0 text-brand-orange" strokeWidth={2.75} data-id="element-156" data-mp="3fpgemg3p5902" />
                </span>
              </div>
            </div>
          </div>

          {/* Mobile/tablet: prominent downward flow cue */}
          <div className="flex justify-center py-6 xl:hidden" aria-hidden data-id="element-157" data-mp="2cgwyepskcjbv">
            <span className="flex flex-col items-center" data-id="element-158" data-mp="1yh4bz6o2kp9p">
              <span className={`h-8 w-[3px] ${RAIL}`} data-id="element-159" data-mp="1ygm6rkjfj0pn" />
              <ArrowDownIcon className="-mt-3 h-8 w-8 text-brand-orange" strokeWidth={2.75} data-id="element-160" data-mp="23ffj39mzclo8" />
            </span>
          </div>

          {/* SYSTEM: WebsiteForge platform + team modules */}
          <div className="min-w-0" data-id="element-161" data-mp="1thj3o5n0f4kb">
            <p className="flex items-center gap-2 text-[12px] font-extrabold uppercase leading-5 text-brand-greenDeep" data-id="element-162" data-mp="1tiko30px13ep">
              <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-brand-orange" aria-hidden data-id="element-163" data-mp="1fs7deqln3alo" />
              WebsiteForge platform + team
            </p>
            <h3 className="mt-1.5 text-[22px] font-extrabold leading-tight text-ink md:text-[26px]" data-id="element-164" data-mp="15rx1raf96b8m">
              Built around you. Managed together.
            </h3>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2 sm:gap-4" data-id="element-165" data-mp="r5hw57962pd7">
              {systemCapabilities.map((cap, i) => {
              const Icon = icons[cap.id as keyof typeof icons];
              const accent = i % 2 === 0 ? 'bg-brand-orange' : 'bg-brand-green';
              const iconColor = i % 2 === 0 ? 'text-brand-orange' : 'text-brand-green';
              return <li key={cap.id} className="flex flex-col rounded-lg border border-ink-line bg-ink p-5 shadow-[0_10px_24px_-16px_rgba(14,17,22,0.6)]" data-id="element-166" data-mp="w6nnb0n6i4km" data-mpi={i}>
                    <div className="flex items-center gap-3" data-id="element-167" data-mp="1ju5m3g4v22x3">
                      <Icon className={`h-6 w-6 shrink-0 ${iconColor}`} strokeWidth={1.9} aria-hidden data-id="element-168" data-mp="1jtnk02viouh9" />
                      <h4 className="text-[17px] font-bold leading-snug text-white" data-id="element-169" data-mp="1evucn6ih9ufe">{cap.title}</h4>
                    </div>
                    <span className={`mt-3 h-[3px] w-8 rounded-full ${accent}`} aria-hidden data-id="element-170" data-mp="1tpytym7mx88j" />
                    <p className="mt-3 text-[16px] leading-[1.55] text-white/75" data-id="element-171" data-mp="1orf7mkp4pufn">{cap.body}</p>
                  </li>;
            })}
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-rule pt-6 sm:flex-row sm:items-center sm:justify-between sm:gap-8" data-id="element-172" data-mp="20tgm3d39nvq2">
          <p className="text-[20px] font-extrabold leading-snug text-ink md:text-[22px]" data-id="element-173" data-mp="2aluom82656da">
            One coordinated system. One accountable team.
          </p>
          <a href="#contact" className="group inline-flex min-h-[44px] shrink-0 items-center gap-2 self-start whitespace-nowrap text-[16px] font-bold text-ink underline decoration-brand-orange decoration-2 underline-offset-[6px] transition-colors duration-150 ease-out hover:decoration-ink sm:self-auto" data-id="element-174" data-mp="2fmicikv3p73a">
            Talk through what you need
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden data-id="element-175" data-mp="u8nqdjnfdg1b" />
          </a>
        </div>
      </div>
    </section>;
}